import React, { useRef, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small interactive map to confirm/adjust a business location. Click or drag
// the marker; parent controls lat/lng and can drive it (e.g. after geocode).
export default function MapPointPicker({ lat, lng, centerLat, centerLng, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const cLng = Number.isFinite(lng) ? lng : (Number.isFinite(centerLng) ? centerLng : 24.6);
    const cLat = Number.isFinite(lat) ? lat : (Number.isFinite(centerLat) ? centerLat : 56.88);
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: `${API}/map/style`,
      center: [cLng, cLat],
      zoom: (Number.isFinite(lat) && Number.isFinite(lng)) ? 15
        : ((Number.isFinite(centerLat) && Number.isFinite(centerLng)) ? 13 : 6.5),
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // Dialogs animate in, so the container can start at 0px — resize once settled.
    setTimeout(() => { try { map.resize(); } catch { /* ignore */ } }, 250);

    const ensureMarker = (lngLat) => {
      if (!markerRef.current) {
        markerRef.current = new maplibregl.Marker({ color: '#D97736', draggable: true }).setLngLat(lngLat).addTo(map);
        markerRef.current.on('dragend', () => {
          const p = markerRef.current.getLngLat();
          onChangeRef.current?.(+p.lat.toFixed(6), +p.lng.toFixed(6));
        });
      } else {
        markerRef.current.setLngLat(lngLat);
      }
    };
    if (lat != null && lng != null) ensureMarker([lng, lat]);
    map.on('click', (e) => {
      ensureMarker([e.lngLat.lng, e.lngLat.lat]);
      onChangeRef.current?.(+e.lngLat.lat.toFixed(6), +e.lngLat.lng.toFixed(6));
    });
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drive the marker/view when lat/lng change externally (e.g. geocoded).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || lat == null || lng == null) return;
    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ color: '#D97736', draggable: true }).setLngLat([lng, lat]).addTo(map);
      markerRef.current.on('dragend', () => {
        const p = markerRef.current.getLngLat();
        onChangeRef.current?.(+p.lat.toFixed(6), +p.lng.toFixed(6));
      });
    } else {
      markerRef.current.setLngLat([lng, lat]);
    }
    map.flyTo({ center: [lng, lat], zoom: 14 });
  }, [lat, lng]);

  return (
    <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: 220 }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="map-point-picker" />
    </div>
  );
}
